import { useEffect, useRef } from 'react'

/**
 * Clean up stale cache-busting search params (_asset_reload) from the URL bar
 * once the React application has successfully mounted.
 */
export const cleanAssetReloadUrl = () => {
  try {
    const url = new URL(window.location.href)
    if (url.searchParams.has('_asset_reload')) {
      url.searchParams.delete('_asset_reload')
      const cleanUrl = url.pathname + (url.search ? url.search : '') + url.hash
      window.history.replaceState(window.history.state, '', cleanUrl)
    }
  } catch (e) {
    // Ignore URL manipulation failures in restricted environments
  }
}

/**
 * Performs a hard, cache-busting reload by appending a fresh timestamp parameter
 * and using location.replace to force browser/CDN cache bypass for new builds.
 * Implements a 2s pause and rate limiting (max 3 reloads within 15s) to prevent rapid loops.
 */
export const performHardReload = (delayMs = 2000) => {
  const now = Date.now()
  const lastReload = Number(sessionStorage.getItem('last_asset_reload_time') || 0)
  const reloadCount = Number(sessionStorage.getItem('asset_reload_count') || 0)

  if (now - lastReload < 15000) {
    if (reloadCount >= 3) {
      console.warn('[AssetRecovery] Rate limit reached. Pausing auto-reloads.')
      return
    }
    sessionStorage.setItem('asset_reload_count', String(reloadCount + 1))
  } else {
    sessionStorage.setItem('asset_reload_count', '1')
  }

  sessionStorage.setItem('last_asset_reload_time', String(now))
  sessionStorage.setItem('asset-reload-attempted', '1')

  setTimeout(() => {
    try {
      const url = new URL(window.location.href)
      url.searchParams.set('_asset_reload', String(Date.now()))
      window.location.replace(url.toString())
    } catch (e) {
      window.location.reload()
    }
  }, delayMs)
}

/**
 * Hook that monitors for new builds and auto-reloads if the build version changes.
 * This prevents "failed to load chunk" errors by detecting when assets have been replaced.
 */
export const useBuildVersionCheck = () => {
  const versionCheckIntervalRef = useRef(null)
  const reloadScheduledRef = useRef(false)

  useEffect(() => {
    // Clean up stale _asset_reload parameter from URL bar on app mount
    cleanAssetReloadUrl()

    // Initial fetch of build version
    const fetchCurrentVersion = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
        if (response.ok) {
          const data = await response.json()
          sessionStorage.setItem('buildVersion', data.version || data.timestamp || 'unknown')
        }
      } catch (error) {
        console.warn('Failed to fetch build version:', error)
      }
    }

    // Check if build version has changed
    const checkForNewBuild = async () => {
      try {
        const response = await fetch('/version.json?t=' + Date.now(), { cache: 'no-store' })
        if (response.ok) {
          const data = await response.json()
          const newVersion = data.version || data.timestamp || 'unknown'
          const currentVersion = sessionStorage.getItem('buildVersion')

          if (currentVersion && currentVersion !== newVersion && !reloadScheduledRef.current) {
            console.info('New build detected. Triggering hard reload...')
            reloadScheduledRef.current = true
            setTimeout(() => {
              performHardReload(2000)
            }, 500)
          }
        }
      } catch (error) {
        console.warn('Error checking for new build:', error)
      }
    }

    // Fetch initial version immediately
    fetchCurrentVersion()

    // Check for new builds every 60 seconds
    versionCheckIntervalRef.current = setInterval(checkForNewBuild, 60000)

    return () => {
      if (versionCheckIntervalRef.current) {
        clearInterval(versionCheckIntervalRef.current)
      }
    }
  }, [])
}

/**
 * Global handler for chunk load failures.
 * When a chunk fails to load (usually because files were deleted after new build),
 * reload the page after a short delay.
 */
export const setupChunkErrorHandler = () => {
  cleanAssetReloadUrl()

  // Handle Vite-specific preload errors
  window.addEventListener('vite:preloadError', (event) => {
    event.preventDefault()
    const reloadScheduled = sessionStorage.getItem('asset-reload-attempted') === '1'
    if (!reloadScheduled) {
      console.warn('Vite preload error. Triggering hard reload...')
      performHardReload(500)
    }
  })

  // Handle dynamic imports that fail
  window.addEventListener('error', (event) => {
    if (
      event.message &&
      (event.message.includes('Failed to fetch') ||
        event.message.includes('Failed to import') ||
        event.message.includes('NetworkError') ||
        event.filename?.includes('.js'))
    ) {
      // Check if this is a chunk load error (404 or network)
      if (event.type === 'error' || (event.message && event.message.includes('Failed'))) {
        const reloadScheduled = sessionStorage.getItem('asset-reload-attempted') === '1'

        if (!reloadScheduled) {
          console.warn('Chunk load failed. Triggering hard reload...')
          performHardReload()
        }
      }
    }
  }, true)

  // Handle unhandled promise rejections for dynamic imports
  window.addEventListener('unhandledrejection', (event) => {
    if (
      event.reason &&
      (typeof event.reason === 'string' || (event.reason.message && event.reason.message.includes('Failed to fetch')))
    ) {
      const errorStr = typeof event.reason === 'string' ? event.reason : event.reason.message || ''
      if (
        errorStr.includes('Failed to fetch') ||
        errorStr.includes('NetworkError') ||
        errorStr.includes('404')
      ) {
        const reloadScheduled = sessionStorage.getItem('asset-reload-attempted') === '1'

        if (!reloadScheduled) {
          console.warn('Failed to load application chunk. Triggering hard reload...')
          performHardReload()
        }
        // Prevent the error from being logged to console
        event.preventDefault()
      }
    }
  })
}
